import React, { useMemo, useState } from "react";
import { Input, PageTitle, PageWrapper, Spinner } from "@cinesys/ui/components";
import { Search } from "react-feather";
import styled from "styled-components";
import { useQuery } from "react-query";
import ApiService from "../../services/ApiService";
import { developerToolsEnabled } from "../../services/env";
import {
  LOW_BALANCE_THRESHOLD,
  isPrepaidPlan,
  needsAttention as isNeedsAttention,
  planLabel,
  planNotes,
} from "./billingPlan";

/**
 * Balances are grouped by what an admin can act on: in the red, low, unavailable, good.
 * Pro / Enterprise orgs are prepaid, so a low balance there isn't flagged (see billingPlan.ts).
 * "cinesys" orgs are internal, so they're hidden in prod but kept on dev/staging for testing.
 */

interface Billing {
  systemDomainId: string;
  title: string;
  balance: string;
  plan?: string | null;
  status?: string | null;
  type?: string | null;
  contractEndDate?: string | null;
}

interface ParsedBilling extends Billing {
  amount: number | null;
}

const Caption = styled.small`
  color: var(--gray100);
  font-size: 13px;
  margin-left: 12px;
`;

const Eyebrow = styled.div`
  font-family: ui-monospace, Menlo, monospace;
  font-size: 12px;
  font-weight: 500;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--gray100);
  margin-bottom: 12px;
`;

const SearchWrap = styled.div`
  max-width: 320px;
  margin-bottom: 22px;
`;

const StatStrip = styled.div`
  display: flex;
  gap: 15px;
  margin-bottom: 28px;
  flex-wrap: wrap;
`;

const StatChip = styled.div`
  background-color: var(--dark100);
  border: 1px solid var(--dark300);
  border-radius: 10px;
  padding: 12px 18px;
  min-width: 140px;
`;

const StatCount = styled.div<{ $tone: string }>`
  font-size: 28px;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  line-height: 1.15;
  color: var(--${(p) => p.$tone}400);
`;

const StatLabel = styled.div`
  font-size: 12px;
  color: var(--gray100);
  margin-top: 2px;
`;

const Section = styled.div`
  margin-bottom: 30px;
`;

const AllClear = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  color: var(--green400);
  font-size: 14px;
  background-color: var(--greenBackground);
  border: 1px solid var(--greenBorder);
  border-radius: 10px;
  padding: 14px 16px;
`;

const EmptyNote = styled.div`
  color: var(--gray100);
  font-size: 13px;
`;

const CardGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(230px, 1fr));
  gap: 12px;
`;

const Card = styled.div<{ $tone: string }>`
  background-color: var(--dark110);
  border: 1px solid var(--dark300);
  border-left: 3px solid var(--${(p) => p.$tone}400);
  border-radius: 8px;
  padding: 14px 16px;
`;

const OrgName = styled.div<{ $flush?: boolean }>`
  font-size: 14px;
  font-weight: 600;
  color: var(--gray400);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  margin-bottom: ${(p) => (p.$flush ? 0 : 8)}px;
`;

const BalanceFigure = styled.div<{ $tone: string }>`
  font-size: 22px;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  color: var(--${(p) => p.$tone}400);
`;

const UnavailableNote = styled.div`
  font-size: 13px;
  color: var(--orange400);
`;

const GoodGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const GoodPill = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  background-color: var(--dark100);
  border: 1px solid var(--dark300);
  border-radius: 20px;
  padding: 6px 14px;
  font-size: 13px;
`;

const PlanBadge = styled.span`
  display: inline-block;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: var(--blue400);
  border: 1px solid var(--blue400);
  border-radius: 10px;
  padding: 1px 8px;
  margin-left: 8px;
  white-space: nowrap;
`;

const PlanNote = styled.span`
  font-size: 12px;
  color: var(--orange400);
  margin-left: 6px;
`;

const CardHeader = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 8px;
  min-width: 0;
`;

const PlanTags = ({ org }: { org: Billing }) => {
  const label = planLabel(org.plan);
  if (!label) {
    return null;
  }
  return (
    <>
      <PlanBadge>{label}</PlanBadge>
      {planNotes(org).map((note) => (
        <PlanNote key={note}>{note}</PlanNote>
      ))}
    </>
  );
};

const GoodName = styled.span`
  color: var(--gray300);
`;

const GoodBalance = styled.span`
  color: var(--green300);
  font-variant-numeric: tabular-nums;
  font-weight: 600;
`;

const PrepaidBalance = styled(GoodBalance)`
  color: var(--gray300);
`;

/** The backend always formats a resolved balance as `Intl.NumberFormat` currency text, e.g.
 *  `"-$45.00 (USD)"` — always starting with an optional "-" then "$". Anything else ("Unknown",
 *  or a raw `JSON.stringify`'d iconik error) means the lookup failed, not a real figure. */
function parseBalance(balance: string): number | null {
  const match = /^(-?)\$([\d,]+\.\d{2})/.exec(balance);
  if (!match) {
    return null;
  }
  const amount = parseFloat(match[2].replace(/,/g, ""));
  return match[1] === "-" ? -amount : amount;
}

const byAmountAsc = (a: ParsedBilling, b: ParsedBilling) =>
  (a.amount ?? 0) - (b.amount ?? 0);
const byTitle = (a: ParsedBilling, b: ParsedBilling) =>
  a.title.localeCompare(b.title);

const BillingListScreen = () => {
  const { data, isLoading, isError } = useQuery({
    queryKey: ["billingList"],
    queryFn: async () => {
      const response = await ApiService.fetch<{ data: Billing[] }>(
        "GET",
        `/api/config/organization/billing`
      );
      return response.data;
    },
  });

  const [search, setSearch] = useState("");
  const isSearching = search.trim().length > 0;

  const { needsAttention, unavailable, goodStanding, prepaid } = useMemo(() => {
    const query = search.trim().toLowerCase();
    const parsed: ParsedBilling[] = (data ?? [])
      .filter(
        (organization) =>
          developerToolsEnabled ||
          !organization.title.toLowerCase().includes("cinesys")
      )
      .filter((organization) =>
        organization.title.toLowerCase().includes(query)
      )
      .map((organization) => ({
        ...organization,
        amount: parseBalance(organization.balance),
      }));

    return {
      needsAttention: parsed.filter(isNeedsAttention).sort(byAmountAsc),
      prepaid: parsed
        .filter((o) => o.amount !== null && isPrepaidPlan(o.plan))
        .sort(byTitle),
      unavailable: parsed.filter((o) => o.amount === null).sort(byTitle),
      goodStanding: parsed
        .filter(
          (o) =>
            o.amount !== null &&
            o.amount >= LOW_BALANCE_THRESHOLD &&
            !isPrepaidPlan(o.plan)
        )
        .sort(byAmountAsc),
    };
  }, [data, search]);

  return (
    <div>
      <PageTitle
        title="Billing"
        error={isError ? "Problem loading billing" : undefined}
        titleChildren={<Caption>Iconik platform balance per org</Caption>}
      ></PageTitle>

      {isLoading ? (
        <Spinner />
      ) : (
        !isError && (
          <PageWrapper>
            <SearchWrap>
              <Input
                value={search}
                onChange={setSearch}
                placeholder="Search organizations"
                icon={Search}
                clearable
              />
            </SearchWrap>

            <StatStrip>
              <StatChip>
                <StatCount $tone="gray">
                  {needsAttention.length +
                    unavailable.length +
                    goodStanding.length +
                    prepaid.length}
                </StatCount>
                <StatLabel>Organizations</StatLabel>
              </StatChip>
              <StatChip>
                <StatCount $tone={needsAttention.length > 0 ? "red" : "green"}>
                  {needsAttention.length}
                </StatCount>
                <StatLabel>Need attention</StatLabel>
              </StatChip>
              <StatChip>
                <StatCount $tone="green">{goodStanding.length}</StatCount>
                <StatLabel>In good standing</StatLabel>
              </StatChip>
            </StatStrip>

            {!(isSearching && needsAttention.length === 0) && (
              <Section>
                <Eyebrow>Needs attention</Eyebrow>
                {needsAttention.length === 0 ? (
                  <AllClear>
                    Every pay-as-you-go organization is at $
                    {LOW_BALANCE_THRESHOLD} or above. Pro and Enterprise plans
                    are listed separately.
                  </AllClear>
                ) : (
                  <CardGrid>
                    {needsAttention.map((org) => (
                      <Card
                        key={org.systemDomainId}
                        $tone={org.amount! < 0 ? "red" : "green"}
                      >
                        <OrgName title={org.title}>{org.title}</OrgName>
                        <BalanceFigure
                          $tone={org.amount! < 0 ? "red" : "green"}
                        >
                          {org.balance}
                        </BalanceFigure>
                      </Card>
                    ))}
                  </CardGrid>
                )}
              </Section>
            )}

            {goodStanding.length > 0 && (
              <Section>
                <Eyebrow>In good standing</Eyebrow>
                <GoodGrid>
                  {goodStanding.map((org) => (
                    <GoodPill key={org.systemDomainId}>
                      <GoodName>{org.title}</GoodName>
                      <GoodBalance>{org.balance}</GoodBalance>
                    </GoodPill>
                  ))}
                </GoodGrid>
              </Section>
            )}

            {prepaid.length > 0 && (
              <Section>
                <Eyebrow>Pro &amp; Enterprise plans</Eyebrow>
                <GoodGrid>
                  {prepaid.map((org) => (
                    <GoodPill key={org.systemDomainId}>
                      <GoodName>{org.title}</GoodName>
                      <PrepaidBalance>{org.balance}</PrepaidBalance>
                      <PlanTags org={org} />
                    </GoodPill>
                  ))}
                </GoodGrid>
              </Section>
            )}

            {!(isSearching && unavailable.length === 0) && (
              <Section>
                <Eyebrow>Balance unavailable</Eyebrow>
                {unavailable.length === 0 ? (
                  <EmptyNote>Every balance resolved successfully.</EmptyNote>
                ) : (
                  <CardGrid>
                    {unavailable.map((org) => (
                      <Card key={org.systemDomainId} $tone="orange">
                        <CardHeader>
                          <OrgName title={org.title} $flush>
                            {org.title}
                          </OrgName>
                          <PlanTags org={org} />
                        </CardHeader>
                        <UnavailableNote>
                          Couldn&rsquo;t look up balance
                        </UnavailableNote>
                      </Card>
                    ))}
                  </CardGrid>
                )}
              </Section>
            )}
          </PageWrapper>
        )
      )}
    </div>
  );
};

export default BillingListScreen;
